/* eslint-disable @typescript-eslint/array-type */
/* eslint-disable no-console */

import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {
  blockAccessTypes,
  blockModelMessages
} from '../../constants/blockPageConstatnts';
import {SampleExcel} from '../../constants/SampleExcel';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import holding from '../../services/modules/JSON/holding';
import holdingDetails from '../../services/modules/OM/holdingDetails';
import listingHoldingDetails from '../../services/modules/OM/listingHolding';
import getTotalUserCount from '../../services/modules/User/totalUserCount';
import Year from '../../shared/Year';
import useCompanyStore, {
  accessModule,
  countModule
} from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  checkCurrentYearOrBelow,
  hasUserRole,
  UserRole
} from '../../utils/commonFunctions';
// import {yearSelect} from '../../utils/FormUtils/yearSelect';
import mapAndFilter from '../../utils/mapCountry';
import {
  // getAvailableDataLimit,
  getModuleDataLimit,
  hasMaxLimitReached
} from '../../utils/SubscriptionHelper';
import BlockAccessPage from '../BlockAccess/BlockAccessPage';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {
  OwnerShipKey,
  OwnerShipLabel
} from '../Forms/Constants/IP/OwnerShipKeyLabel';
import OwnerShipForm from '../Forms/OwnershipForm';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {isValueOrNot} from './Utils/filterReturn';
import {createBulkRowSelection} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';

const OwnershipTable: React.FC = () => {
  interface DataType {
    key: React.Key;
    _id: string;
    type: number;
    name: string;
    sharesPercentage: number;
  }

  const [searchParam, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const companyId = useCompanyStore.getState().companyId;
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const holdingType = useJSONStore.getState().holdingType;
  const [isLoading, setIsLoading] = useState(true);
  // const firstYear = yearSelect();

  useEffect(() => {
    const currentYear = checkCurrentYearOrBelow(
      searchParam.get('selectedYear')
    );
    setSelectedYear(currentYear);
    searchParam.set('selectedYear', currentYear);
    setSearchParams(searchParam);
    Promise.all([
      // holdingDetails(
      //   APITypeEnum.GET_ALL,
      //   undefined,
      //   `${companyId}/${selectedYear}`
      // ),
      holding(),
      getTotalUserCount()
    ])
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    holdingDetails(APITypeEnum.DELETE_ALL, {
      holdings: _id,
      year: selectedYear
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      Promise.all([
        listingHoldingDetails(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`,
          'valueChain'
        ),
        getTotalUserCount()
      ])
        .then(([main]) => {
          setDataSource(main);
        })
        .catch(() => {})
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    if (selectedYear) {
      setIsRefresh(true);
      Promise.all([
        listingHoldingDetails(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`,
          'valueChain'
        )
      ])
        .then(([res]) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          ?.toLowerCase()
          ?.includes(value?.toString()?.toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const OwnerShipHoldingKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: OwnerShipKey.HOLDING_NAME,
      label: OwnerShipLabel.HOLDING_NAME,
      type: objTypes.string
    },
    {
      key: OwnerShipKey.CREATE_USER,
      label: OwnerShipLabel.IS_USER_CREATION,
      type: objTypes.userCreation,
      relatedKey: OwnerShipKey.EMAIL
    },
    {
      key: OwnerShipKey.FIRST_NAME,
      label: OwnerShipLabel.FIRST_NAME,
      type: objTypes.string,
      relatedKey: OwnerShipKey.EMAIL,
      checkRelatedKeyFalsy: true
    },
    {
      key: OwnerShipKey.LAST_NAME,
      label: OwnerShipLabel.LAST_NAME,
      type: objTypes.string,
      relatedKey: OwnerShipKey.EMAIL,
      checkRelatedKeyFalsy: true
    },
    {
      key: OwnerShipKey.HOLDING_TYPE,
      label: OwnerShipLabel.HOLDING_TYPE,
      type: objTypes.json,
      format: holdingType
    },
    {
      key: OwnerShipKey.HOLDING_PERCENT,
      label: OwnerShipLabel.HOLDING_PERCENT,
      type: objTypes.percentage
    },
    {
      key: OwnerShipKey.TELEPHONE,
      label: OwnerShipLabel.TELEPHONE,
      type: objTypes.string,
      relatedKey: OwnerShipKey.EMAIL,
      checkRelatedKeyFalsy: true
    },
    {
      key: OwnerShipKey.EMAIL,
      label: OwnerShipLabel.EMAIL,
      type: objTypes.string,
      relatedKey: OwnerShipKey.EMAIL,
      checkRelatedKeyFalsy: true
    },
    {
      key: OwnerShipKey.GST,
      label: OwnerShipLabel.GST,
      type: objTypes.string
    }
  ];

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = [
    {
      title: OwnerShipLabel.HOLDING_NAME,
      dataIndex: OwnerShipKey.HOLDING_NAME,
      editable: true,
      // width: '100px',
      ...SearchFilter(
        OwnerShipLabel.HOLDING_NAME,
        OwnerShipKey.HOLDING_NAME as keyof DataType
      ),
      render: (value) => {
        return isValueOrNot(value);
      }
    },
    {
      title: OwnerShipLabel.HOLDING_TYPE,
      dataIndex: OwnerShipKey.HOLDING_TYPE,
      // width: '120px',
      editable: true,
      type: 'select',
      filterer: holdingType,
      filters: mapAndFilter(
        dataSource,
        holdingType,
        OwnerShipKey.HOLDING_TYPE
      ).map((item) => {
        return {text: item.label, value: item.value};
      }),
      onFilter: (value, record) => {
        return record[OwnerShipKey.HOLDING_TYPE as keyof DataType] === value;
      },
      render: (value) => {
        return (
          <>
            {holdingType.find((item) => {
              return item.value === value;
            })?.label ?? '-'}
          </>
        );
      }
    },
    {
      title: OwnerShipLabel.HOLDING_PERCENT,
      type: 'number',
      dataIndex: OwnerShipKey.HOLDING_PERCENT,
      ...SearchFilter(
        OwnerShipLabel.HOLDING_PERCENT,
        OwnerShipKey.HOLDING_PERCENT as keyof DataType
      ),
      onFilter: (value, record: any) => {
        const recordValue = record[OwnerShipKey.HOLDING_PERCENT];

        return (
          (recordValue &&
            value &&
            recordValue
              .toString()
              .toLowerCase()
              .includes(value.toString().toLowerCase())) ??
          false
        );
      },
      editable: true,
      render: (value) => {
        return `${isValueOrNot(value)} ${value ? '%' : ''}`;
      }
    },
    ...(!hasUserRole(UserRole.View_Only)
      ? ([
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '',
            fixed: 'right',
            width: '200px',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  isConfigurator={true}
                />
              ) : null
          }
        ] as any)
      : [])
  ];

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {searchParam.get('selectedYear') && (
        <>
          <Year setSelectedYear={setSelectedYear} />
        </>
      )}
      <TableTitleHeader
        title="Holding Details"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Holding'
        }}
        sampleDownload={{href: SampleExcel.holding}}
        jobs={{setShowJobs, uploadType: uploadTypes.OwnershipInfo}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        isTable={true}
        onlyYear={true}
        shouldEdit={!hasUserRole(UserRole.View_Only)}
        onSelectAll={
          !hasUserRole(UserRole.View_Only) ? handleSelectAll : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        blockAddition={hasMaxLimitReached(countModule.externalStakeholders)}
        shouldDisable={refresh}
        showUserCreationUploadConfirm
        // accessModuleType={accessModule.externalStakeholders}
        // accessModuleDataLimit={getAvailableDataLimit(
        //   countModule.externalStakeholders
        // )}
      />
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.OwnershipInfo}
        details={`null/${selectedYear}`}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          }
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: isRefresh
        }}
      />
      {hasMaxLimitReached(countModule.externalStakeholders) ? (
        <div style={{marginTop: 15, marginBottom: 10}}>
          <BlockAccessPage
            blockedType={blockAccessTypes.PARTIAL}
            messageTitle={
              blockModelMessages.externalStakeholder.title.limitReached
            }
            messageDescription={blockModelMessages.externalStakeholder.description.limitReached(
              getModuleDataLimit(accessModule.externalStakeholders)
            )}
          />
        </div>
      ) : null}
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={<OwnerShipForm data={isEdit ? editData : undefined} />}
        API={holdingDetails}
        stepsTitle={['Add Holding']}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        onlyYear={true}
        formPreview={OwnerShipHoldingKeyLabel}
        isConfigurator={true}
      />
    </>
  );
};

export default OwnershipTable;
